<script>
import { format as formatDate } from '@/utils/date';
import { format as formatNumber } from '@/utils/number';
import { truncate } from '@/utils/strings';
import HollowDotsSpinner from '@/components/common/HollowDotsSpinner.vue';
import ColumnWithHelp from '@/components/advertisement/common/ColumnWithHelp.vue';

// TODO: remove this when we have a better way to get campaign types
import { campaignTypes } from '../Campaigns/common/constants';
import {
  campaignStatus,
  campaignTypes as campaignTypesEnum,
} from './constants';

import lang from '@/mixins/lang';
import { translateCampaignStatus } from './utility/translators/translateCampaignStatus';

export default {
  name: 'CampaignsTable',
  components: {
    ColumnWithHelp,
    HollowDotsSpinner,
  },
  mixins: [lang],
  props: {
    campaigns: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    statsData: {
      type: Array,
      default: () => [],
    },
    statsLoading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    truncate,
    formatNumber,
    translateCampaignStatus,
    calcBudgetPercentage({ remainingBudget, totalBudget }) {
      return (remainingBudget * 100) / totalBudget;
    },
    formatDate(ts) {
      const formatPattern = this.lang === 'fa' ? 'jYYYY/jMM/jDD' : 'YYYY/MM/DD';
      return formatDate(ts, formatPattern, 'X');
    },
    getCampaignType(type) {
      const typeKey = campaignTypes[type];
      if (!typeKey) return '؟';
      return this.$t(`ADVERTISEMENT.CAMPAIGNS.${typeKey}`);
    },
    generateRowActions(campaign) {
      return [
        {
          title: this.$t('ADVERTISEMENT.CAMPAIGNS.DETAILS'),
          handler: () => this.navigateToCampaignPage(campaign),
        },
        !campaign.isPaused && {
          title: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSE'),
          handler: () => this.$emit('pause-campaign', campaign.uuid),
        },
        campaign.isPaused && {
          title: this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVATE'),
          handler: () => this.$emit('activate-campaign', campaign.uuid),
        },
      ];
    },
    navigateToCampaignPage({ campaignType, uuid, oldId } = {}) {
      const campaignRoutes = {
        D: 'discovery-ads-campaign-info',
        S: 'search-ads-campaign-info',
        B: 'basic-ads-campaign-info',
      };

      this.$router.push({
        name: campaignRoutes[campaignType],
        params: { campaignId: oldId || uuid },
      });
    },
    statsFallbackValue(campaign) {
      return campaign.campaignType === campaignTypesEnum.SMART ? '-' : 0;
    },
    updatePauseState(id, activate) {
      if (activate) {
        this.$emit('activate-campaign', id);
      } else {
        this.$emit('pause-campaign', id);
      }
    },
    canChangeActiveState(campaign) {
      if (this.readonly) return false;
      const changeableStates = [campaignStatus.ACTIVE, campaignStatus.PAUSED];
      return changeableStates.includes(campaign.status);
    },
  },
};
</script>

<template>
  <ElTable
    :data="campaigns"
    class="campaigns-table"
    show-summary
    @row-click="navigateToCampaignPage"
    @sort-change="$emit('sort-change', $event)"
  >
    <ColumnWithHelp
      v-slot="{ row }"
      width="120"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.STATUS')"
      en-translation="Status"
    >
      <ElSwitch
        v-if="canChangeActiveState(row)"
        :value="!row.isPaused"
        @click.native.stop
        @change="updatePauseState(row.uuid, $event)"
      />
      <span v-else>{{ translateCampaignStatus(row.status) }}</span>
    </ColumnWithHelp>

    <ColumnWithHelp
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGN_VIEW.CAMPAIGN_NAME')"
      prop="name"
      min-width="200"
      en-translation="Campaign Name"
    />

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.TYPE')"
      min-width="140"
      en-translation="Campaign Type"
      align="center"
    >
      {{ getCampaignType(row.campaignType) }}
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGN_FINANCE.REMAINING_BUDGET')"
      prop="remainingBudget"
      min-width="170"
      en-translation="Remaining Budget"
      :sortable="true"
      align="center"
    >
      <HollowDotsSpinner
        v-show="statsLoading"
        color="#ccc"
        :dot-size="5"
        filled
        variant="text"
      />

      <div v-if="!statsLoading">
        <span dir="ltr" :class="{ 'text-danger': row.remainingBudget < 0 }">
          {{ formatNumber(row.remainingBudget || 0) }}
        </span>
      </div>
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.TOTAL_IMPRESSIONS')"
      en-translation="Impressions"
      prop="impressions"
      min-width="170"
      :sortable="true"
      align="center"
    >
      <HollowDotsSpinner
        v-show="statsLoading"
        color="#ccc"
        :dot-size="5"
        filled
        variant="text"
      />
      <span v-show="!statsLoading">
        {{ formatNumber(row.impressions || statsFallbackValue(row)) }}
      </span>
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.TOTAL_CLICKS')"
      en-translation="Total Clicks"
      min-width="140"
      prop="views"
      :sortable="true"
      align="center"
    >
      <HollowDotsSpinner
        v-show="statsLoading"
        color="#ccc"
        :dot-size="5"
        filled
        variant="text"
      />
      <span v-show="!statsLoading">
        {{ formatNumber(row.views || statsFallbackValue(row)) }}
      </span>
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.TOTAL_INSTALLS')"
      en-translation="Total Installs"
      prop="installs"
      min-width="150"
      :sortable="true"
      align="center"
    >
      <HollowDotsSpinner
        v-show="statsLoading"
        color="#ccc"
        :dot-size="5"
        filled
        variant="text"
      />
      <span v-show="!statsLoading">
        {{ formatNumber(row.installs || statsFallbackValue(row)) }}
      </span>
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.AVERAGE_COST')"
      en-translation="Average Cost"
      min-width="160"
      prop="avgCost"
      :sortable="true"
      align="center"
    >
      <HollowDotsSpinner
        v-show="statsLoading"
        color="#ccc"
        :dot-size="5"
        filled
        variant="text"
      />
      <span v-show="!statsLoading">{{ formatNumber(row.avgCost || 0) }}</span>
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      min-width="140"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.TOTAL_COST')"
      en-translation="Overall Cost"
      prop="cost"
      :sortable="true"
      align="center"
    >
      <HollowDotsSpinner
        v-show="statsLoading"
        color="#ccc"
        :dot-size="5"
        filled
        variant="text"
      />
      <span v-show="!statsLoading">{{ formatNumber(row.cost || 0) }}</span>
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      min-width="100"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.START_DATE')"
      align="center"
    >
      {{ formatDate(row.start) }}
    </ColumnWithHelp>

    <ColumnWithHelp
      v-slot="{ row }"
      :content="$t('متن راهنما اینجا نمایش داده می‌شود')"
      min-width="100"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.END_DATE')"
    >
      {{ formatDate(row.end) }}
    </ColumnWithHelp>
  </ElTable>
</template>

<style lang="scss">
.campaigns-table {
  font-size: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 12px;

  thead tr,
  th {
    font-weight: bold;
    font-size: 0.8rem;
    background-color: #e5e7f0;
  }

  th.el-table__cell {
    vertical-align: top;
  }

  .cell {
    min-height: 34px;
  }

  .more-button {
    border-width: 0;

    i {
      transform: rotate(90deg);
    }
  }

  svg {
    vertical-align: middle;
  }

  .el-tag {
    border-radius: 100px !important;
  }

  .el-table__row {
    cursor: pointer;
    height: 4.3rem;
  }

  .caret-wrapper {
    margin: 0;
  }

  @include rtl() {
    .cell {
      flex-direction: row-reverse;
      justify-content: right;
    }
  }
}
</style>
